import { ref } from 'vue';

const buildRow = (field = {}) => ({
  custom_field_public_id: field.custom_field_public_id ?? field.id ?? '',
  name: field.name ?? '',
  slug: field.slug ?? '',
  field_type: field.field_type ?? 'text',
  required: field.required ?? false,
  value: field.value ?? '',
});

const buildSlugFromName = name =>
  name
    .toString()
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

const getPersistedFieldKey = (field = {}) => {
  const customFieldPublicId = field.custom_field_public_id?.trim();

  if (customFieldPublicId) {
    return customFieldPublicId;
  }

  const normalizedSlug = field.slug?.trim().toLowerCase();

  if (normalizedSlug) {
    return normalizedSlug;
  }

  const normalizedName = field.name?.trim();

  return normalizedName ? buildSlugFromName(normalizedName) : '';
};

export const useProductSpecifications = () => {
  const drafts = ref([]);

  const addField = field => {
    const row = buildRow(field);
    const rowKey = getPersistedFieldKey(row);

    if (!rowKey) {
      drafts.value.push(row);
      return;
    }

    const alreadyExists = drafts.value.some(
      draftField => getPersistedFieldKey(draftField) === rowKey
    );

    if (!alreadyExists) {
      drafts.value.push(row);
    }
  };

  const removeField = index => {
    drafts.value.splice(index, 1);
  };

  const applyPreset = (preset = {}) => {
    (preset.fields || []).forEach(field => {
      addField(field);
    });
  };

  const hydrate = existingFields => {
    drafts.value = (existingFields || []).map(field => buildRow(field));
  };

  const serialize = () =>
    drafts.value
      .map(field => ({
        custom_field_public_id:
          field.custom_field_public_id?.trim() || undefined,
        name: field.name?.trim() || '',
        slug: field.slug?.trim() || buildSlugFromName(field.name || ''),
        field_type: field.field_type || 'text',
        required: !!field.required,
        value: field.value,
      }))
      .filter(field => field.name || field.custom_field_public_id);

  return {
    drafts,
    addField,
    removeField,
    applyPreset,
    hydrate,
    serialize,
    buildRow,
    getPersistedFieldKey,
  };
};
